import jwtDecode from 'jwt-decode'
import { fetchClient } from './fetch'
import { getQueryParams } from './location'
import { graphqlClient } from './gql'

const authProvider = {
    baseUrl: '/auth',
    token: ((window._catapult || {}).authProvider || {}).token
        || null,
    refreshSession: ((window._catapult || {}).authProvider || {}).refreshSession
        || null,
    refreshInterval: ((window._catapult || {}).authProvider || {}).refreshInterval
        || 430000 - Math.floor(Math.random() * 1001),
    messages: {
        PERMISSIONS_ERROR: 'Access denied'
    },
    bc: (() => {
        if (!('BroadcastChannel' in window))
            return null

        const bc = new window.BroadcastChannel('session_channel')

        bc.onmessage = (e) => {
            const { data } = e || {}

            console.groupCollapsed('authProvider.bc.onmessage')

            console.log(data)

            authProvider.token = data.token || null

            if (!authProvider.token)
                return authProvider.clearSession()

            setTimeout(() => {
                const session = authProvider.getSession() || {}

                session.jwt_token = authProvider.token

                authProvider.setSession(session)
            }, 2000)

            console.groupEnd()
        }

        return bc
    })(),

    getSession: () => window.localStorage.getItem('session')
        && JSON.parse(window.localStorage.getItem('session')),

    setSession: (obj) => {
        obj = obj || {}

        const session = obj.jwt_token
            ? jwtDecode(obj.jwt_token)
            : obj

        if (obj.jwt_token) {
            authProvider.token = obj.jwt_token

            const headers = JSON.parse(JSON.stringify(
                (session['https://hasura.io/jwt/claims'] || {})))

            headers.authorization = `Bearer ${authProvider.token}`

            headers['x-hasura-role']
                = headers['x-hasura-default-role'] === 'admin'
                    ? 'admin'
                    : 'user'

            authProvider.setFetchOptions({ headers })

            authProvider.setRefreshSession()
        }

        if (obj.jwt_token
                && obj.refresh_token)
            session.refresh_token = obj.refresh_token

        if (obj.user)
            session.user = obj.user

        if (session.user
                && session.user.fullName !== session.user.display_name)
            session.user.fullName = session.user.display_name

        if (session.user
                && session.user.avatar !== session.user.avatar_url)
            session.user.avatar = session.user.avatar_url

        if ('remember' in obj)
            session.remember = obj.remember

        console.groupCollapsed(`authProvider.setSession${
            !Object.keys(obj).length
                ? ' - cleared'
                : ''}`)
        console.log('Arguments: obj', obj)
        console.log('Session', session)
        console.log('Token', authProvider.token)
        console.log('authProdivder', authProvider)
        console.groupEnd()

        if (JSON.stringify(session)
                !== JSON.stringify(authProvider.getSession() || {}))
            Object.keys(session).length
                ? window.localStorage
                    .setItem('session', JSON.stringify(session))
                : window.localStorage
                    .removeItem('session')

        return authProvider.token
    },

    setFetchOptions: (fetchOptions = {
        headers: {
            'x-hasura-role': 'anonymous'
        }
    }) => {
        graphqlClient
            .setFetchOptions(fetchOptions)

        fetchClient
            .setFetchOptions(fetchOptions)
    },

    clearSession: () => {
        authProvider.clearRefreshSession()

        authProvider.setSession()

        authProvider.token = null

        authProvider.setFetchOptions()
    },

    login: async ({ email, password, remember }) => {
        console.groupCollapsed('authProvider.login')

        const data = await fetchClient
            .post(`${authProvider.baseUrl}/login`, {
                json: {
                    email,
                    password
                }
            })
            .catch(err => err)

        if (data.error) {
            authProvider.clearSession()

            if (authProvider.bc)
                authProvider.bc.postMessage({
                    id: authProvider.id,
                    token: null
                })

            return Promise.reject(new Error((data.message || 'Unknown error')
                .replace(/Username/g, 'Email')
                .replace(/username/g, 'email')))
        }

        console.groupEnd()

        const token = authProvider
            .setSession(Object.assign(data, { remember }))

        if (authProvider.bc)
            authProvider.bc.postMessage({
                id: authProvider.id,
                token: (data || {}).jwt_token || null
            })

        return Promise.resolve(token)
    },

    logout: () => {
        authProvider.clearSession()

        return Promise.resolve()
    },

    refreshToken: () => {
        console.groupCollapsed('authProvider.refreshToken')

        const queryRefreshToken = (getQueryParams() || {}).refresh_token

        const session = (authProvider.getSession() || {})

        const refreshToken = session.refresh_token
            || queryRefreshToken

        const isExpired = authProvider.isSessionExpired()

        const isClearNonRememberSession = isExpired
            && !session.remember
            && !queryRefreshToken

        const expiresDuration = !isExpired
                && session.exp
            ? (session.exp * 1000) - Date.now()
            : 0

        const expiresRefreshDiff = expiresDuration
            && expiresDuration - authProvider.refreshInterval

        if (expiresRefreshDiff > authProvider.refreshInterval
                && authProvider.token) {
            console.log('Session valid:', session, authProvider)
            console.log(`Expires in MS: ${expiresDuration || 'N/A'}`)
            console.log(`Expires and Refresh Interval Diff in MS: ${
                expiresRefreshDiff || 'N/A'}`)
            console.groupEnd()

            if (!authProvider.refreshSession)
                authProvider.setRefreshSession()

            return Promise.resolve(authProvider.token)
        }

        console.log('Session:', session, authProvider)
        console.log(`Query Refresh Token: ${queryRefreshToken || 'N/A'}`)
        console.log(`Refresh Token: ${refreshToken || 'N/A'}`)
        console.log(`Expired Session: ${isExpired}`)
        console.log(`Expired Short Session: ${isClearNonRememberSession}`)
        console.groupEnd()

        return !refreshToken
                || isClearNonRememberSession
            ? Promise.resolve(authProvider.clearSession())
            : fetchClient
                .get(`${
                    authProvider.baseUrl}/token/refresh?refresh_token=${
                    refreshToken}`)
                .then((obj) => {
                    const sessionObj = Object.assign(obj, {
                        remember: session.remember
                    })

                    const token = authProvider
                        .setSession(sessionObj) || null

                    const bearerToken = token === authProvider.token
                        ? token
                        : null

                    if (authProvider.bc)
                        authProvider.bc.postMessage({
                            id: authProvider.id,
                            token: bearerToken || authProvider.token
                        })

                    if (!bearerToken)
                        authProvider.clearSession()

                    return bearerToken
                })
                .catch(err => {
                    console.warn(err)
                    return authProvider.clearSession()
                })
    },

    clearRefreshSession: () => authProvider.refreshSession
        && clearInterval(authProvider.refreshSession),

    setRefreshSession: () => {
        authProvider.clearRefreshSession()

        authProvider.refreshSession
            = setInterval(() => authProvider.refreshToken(),
                authProvider.refreshInterval)
    },

    checkAuth: () => {
        let error = 'Please login to continue'

        const session = authProvider.getSession() || { user: {} }

        const isRoleAllowed = authProvider.checkRole(
            window.location.pathname.match(/^\/admin\/*/)
                ? 'admin'
                : authProvider.getDefaultRole())

        if (!isRoleAllowed
                && session
                && session.user
                && session.user.id)
            error = authProvider.messages.PERMISSIONS_ERROR

        const isAuthenticated = authProvider.token
            && isRoleAllowed
            && session
            && session.user
            && session.user.id === session.sub
            && session.sub === authProvider.getClaims()['x-hasura-user-id']
            && !authProvider.isSessionExpired()

        console.groupCollapsed('authProvider.checkAuth')
        console.log('Token', authProvider.token)
        console.log('Is Role Allowed', isRoleAllowed)
        console.log('Session User', session.user || session)
        console.log('Session Sub Id', session.sub)
        console.log('X-hasura-user-id',
            authProvider.getClaims()['x-hasura-user-id'])
        console.log('Session Expired',
            authProvider.isSessionExpired(), session.expires)
        console.groupEnd()

        return isAuthenticated
            ? Promise.resolve(true)
            : Promise.reject(new Error(error))
    },

    checkError: (error) => {
        console.warn(error)
    },

    checkRole: (role = 'anonymous') => {
        return role
            && (role === authProvider.getDefaultRole()
                || authProvider.getAllowedRoles().indexOf(role) >= 0
                || (authProvider.isSessionExpired()
                    && role === 'anonymous'))
    },

    getIdentity: () => {
        if (authProvider.isSessionExpired())
            return null

        const { user } = authProvider.getSession() || {}

        if (user
                && !user.fullName)
            user.fullName = user.display_name

        if (user
                && (user.avatar_url || '')
                    .match(/\.gravatar\.com/))
            user.avatar_url = null

        if (user
                && !user.avatar)
            user.avatar = user.avatar_url

        return user || null
    },

    getPermissions: () => {
        const isRoleAllowed = authProvider.checkRole('admin')

        return isRoleAllowed
            ? Promise.resolve()
            : Promise.reject(
                new Error(authProvider.messages.PERMISSIONS_ERROR))
    },

    getClaims: () => {
        const session = authProvider.getSession() || {}
        return (session['https://hasura.io/jwt/claims'] || {})
    },

    getDefaultRole: () => {
        const claims = authProvider.getClaims()
        return claims['x-hasura-default-role']
    },

    getAllowedRoles: () => {
        const claims = authProvider.getClaims()
        return claims['x-hasura-allowed-roles'] || []
    },

    getDisplayName: (fullName = false) => {
        const { user } = authProvider.getSession() || {}

        if (!user)
            return null

        if (fullName)
            return user.display_name

        const userDisplayNameParts = (user.display_name || '')
            .split(' ')

        return userDisplayNameParts[0] || user.display_name
    },

    getInitials: (displayName = '') => {
        const userDisplayNameParts = (displayName
                || authProvider.getDisplayName(true)
                || '')
            .split(' ')
            .filter(str => str)

        if (!userDisplayNameParts.length)
            return null

        const userFirstName = userDisplayNameParts[0]

        const userLastName = userDisplayNameParts.length > 1
            && userDisplayNameParts[userDisplayNameParts.length - 1]

        return `${
            (userFirstName || '').substring(0, 1).toUpperCase()}${
            (userLastName || '').substring(0, 1).toUpperCase()}`
    },

    isSessionExpired: () => {
        const session = (authProvider.getSession() || {})

        return ((session.exp || 0) * 1000) < (new Date()).getTime()
    }
}

export default authProvider
